import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

import Icon from '../../../components/AppIcon';

const SecuritySettings = ({ securityData }) => {
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(securityData?.twoFactorEnabled || false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isToggling2FA, setIsToggling2FA] = useState(false);

  const loginHistory = [
    {
      id: 1,
      device: 'Chrome on Windows',
      location: 'New York, NY, USA',
      ipAddress: '192.168.1.100',
      timestamp: '2024-11-18 09:30:00',
      status: 'success',
      isCurrent: true
    },
    {
      id: 2,
      device: 'Safari on iPhone',
      location: 'New York, NY, USA',
      ipAddress: '192.168.1.101',
      timestamp: '2024-11-17 18:45:00',
      status: 'success',
      isCurrent: false
    },
    {
      id: 3,
      device: 'Chrome on Android',
      location: 'Brooklyn, NY, USA',
      ipAddress: '192.168.1.102',
      timestamp: '2024-11-16 14:20:00',
      status: 'success',
      isCurrent: false
    },
    {
      id: 4,
      device: 'Firefox on Windows',
      location: 'Unknown Location',
      ipAddress: '203.0.113.45',
      timestamp: '2024-11-15 22:15:00',
      status: 'failed',
      isCurrent: false
    }
  ];

  const handlePasswordChange = async () => {
    if (passwordData?.newPassword !== passwordData?.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    setIsChangingPassword(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API call
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);
      alert('Password changed successfully');
    } catch (error) {
      console.error('Error changing password:', error);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleToggle2FA = async () => {
    setIsToggling2FA(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      setTwoFactorEnabled(!twoFactorEnabled);
    } catch (error) {
      console.error('Error toggling 2FA:', error);
    } finally {
      setIsToggling2FA(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDeviceIcon = (device) => {
    if (device?.includes('iPhone') || device?.includes('Android')) return 'Smartphone';
    if (device?.includes('Chrome') || device?.includes('Firefox') || device?.includes('Safari')) return 'Monitor';
    return 'Laptop';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="Lock" size={20} className="text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground">Security Settings</h2>
          <p className="text-sm text-muted-foreground">Manage your account security and authentication</p>
        </div>
      </div>
      <div className="space-y-6">
        {/* Password Section */}
        <div className="border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-medium text-foreground">Password</h3>
              <p className="text-sm text-muted-foreground">Last changed on November 10, 2024</p>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowPasswordForm(!showPasswordForm)}
              iconName="Key"
              iconPosition="left"
              iconSize={16}
            >
              Change Password
            </Button>
          </div>

          {showPasswordForm && (
            <div className="space-y-4 pt-4 border-t border-border">
              <Input
                label="Current Password"
                type="password"
                value={passwordData?.currentPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e?.target?.value }))}
                placeholder="Enter current password"
                required
              />
              <Input
                label="New Password"
                type="password"
                value={passwordData?.newPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e?.target?.value }))}
                placeholder="Enter new password"
                description="Must be at least 8 characters with uppercase, lowercase, and numbers"
                required
              />
              <Input
                label="Confirm New Password"
                type="password"
                value={passwordData?.confirmPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e?.target?.value }))}
                placeholder="Confirm new password"
                required
              />
              <div className="flex items-center space-x-3">
                <Button
                  onClick={handlePasswordChange}
                  loading={isChangingPassword}
                  disabled={!passwordData?.currentPassword || !passwordData?.newPassword || !passwordData?.confirmPassword}
                  iconName="Save"
                  iconPosition="left"
                  iconSize={16}
                >
                  Update Password
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Two-Factor Authentication */}
        <div className="border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="font-medium text-foreground mb-1">Two-Factor Authentication</h3>
              <p className="text-sm text-muted-foreground">
                Add an extra layer of security to your account with SMS or authenticator app
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                twoFactorEnabled 
                  ? 'text-success bg-success/10 border border-success/20' :'text-muted-foreground bg-muted border border-border'
              }`}>
                {twoFactorEnabled ? 'Enabled' : 'Disabled'}
              </div>
              <Button
                variant={twoFactorEnabled ? "outline" : "default"}
                onClick={handleToggle2FA}
                loading={isToggling2FA}
                iconName={twoFactorEnabled ? "ShieldOff" : "Shield"}
                iconPosition="left"
                iconSize={16}
              >
                {twoFactorEnabled ? 'Disable' : 'Enable'} 2FA
              </Button>
            </div>
          </div>
        </div>

        {/* Login History */}
        <div className="border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-medium text-foreground">Login History</h3>
              <p className="text-sm text-muted-foreground">Recent login attempts and device activity</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconName="RefreshCw"
              iconPosition="left"
              iconSize={16}
            >
              Refresh
            </Button>
          </div>

          <div className="space-y-3">
            {loginHistory?.map((login) => (
              <div key={login?.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    login?.status === 'success' ? 'bg-success/10' : 'bg-error/10'
                  }`}>
                    <Icon 
                      name={getDeviceIcon(login?.device)} 
                      size={16} 
                      className={login?.status === 'success' ? 'text-success' : 'text-error'} 
                    />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-foreground">{login?.device}</span>
                      {login?.isCurrent && (
                        <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">Current</span>
                      )}
                      {login?.status === 'failed' && (
                        <span className="text-xs bg-error/10 text-error px-2 py-1 rounded">Failed</span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {login?.location} • {login?.ipAddress}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-foreground">{formatTimestamp(login?.timestamp)}</div>
                  {login?.status === 'failed' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-error hover:text-error"
                      iconName="AlertTriangle"
                      iconPosition="left"
                      iconSize={12}
                    >
                      Report
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Security Recommendations */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Shield" size={16} className="text-primary mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-foreground mb-2">Security Recommendations</h4>
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center space-x-2">
                  <Icon name={twoFactorEnabled ? "CheckCircle" : "Circle"} size={12} className={twoFactorEnabled ? "text-success" : "text-muted-foreground"} />
                  <span>Enable two-factor authentication</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={12} className="text-success" />
                  <span>Use a strong, unique password</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={12} className="text-success" />
                  <span>Keep your contact information updated</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Circle" size={12} className="text-muted-foreground" />
                  <span>Review login history regularly</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;